import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, onSnapshot, updateDoc, serverTimestamp, Timestamp, collection, query, where, getDocs, deleteDoc } from "firebase/firestore";
import { db } from "../../../Firebase";
import styled from "styled-components";
import { V3Room, V3Event, V3User } from "../types/v3types";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
  color: #dc143c;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'running': return '#28a745';
      case 'paused': return '#ffc107';
      case 'finished': return '#6c757d';
      default: return '#007bff';
    }
  }};
  color: white;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  color: #333;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
`;

const InfoItem = styled.div`
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const ControlPanel = styled.div`
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const Button = styled.button<{ variant?: string }>`
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      case 'warning':
        return `
          background: #ffc107;
          color: #212529;
          &:hover { background: #e0a800; }
        `;
      case 'success':
        return `
          background: #28a745;
          color: white;
          &:hover { background: #218838; }
        `;
      default:
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 30px;
  background: #e9ecef;
  border-radius: 15px;
  overflow: hidden;
  margin: 20px 0;
`;

const ProgressFill = styled.div<{ percent: number }>`
  width: ${props => props.percent}%;
  height: 100%;
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const ProblemStatus = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-top: 20px;
`;

const ProblemIndicator = styled.div<{ status: 'locked' | 'current' | 'released' }>`
  padding: 10px;
  text-align: center;
  border-radius: 6px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'current': return '#28a745';
      case 'released': return '#007bff';
      default: return '#e9ecef';
    }
  }};
  color: ${props => props.status === 'locked' ? '#666' : 'white'};
`;

const CountdownDisplay = styled.div`
  font-size: 48px;
  font-weight: bold;
  text-align: center;
  color: #dc143c;
  margin: 20px 0;
`;

const AdminKachidokiManager: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [participants, setParticipants] = useState<V3User[]>([]);
  const [countdown, setCountdown] = useState<number>(0);
  const [nextReleaseIn, setNextReleaseIn] = useState<number>(0);

  useEffect(() => {
    if (!roomId) return;

    const unsubscribe = onSnapshot(doc(db, "v3rooms", roomId), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const roomData = docSnapshot.data() as V3Room;
        setRoom(roomData);

        // Load event data
        if (roomData.eventId) {
          const eventUnsubscribe = onSnapshot(
            doc(db, "v3events", roomData.eventId),
            (eventDoc) => {
              if (eventDoc.exists()) {
                setEvent(eventDoc.data() as V3Event);
              }
            }
          );
          return () => eventUnsubscribe();
        }
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    if (!room || !event) return;

    // Subscribe to participants
    const participantIds = room.participants || [];
    if (participantIds.length > 0) {
      const unsubscribes = participantIds.map((userId) =>
        onSnapshot(doc(db, "v3users", userId), (doc) => {
          if (doc.exists()) {
            const userData = doc.data() as V3User;
            setParticipants((prev) => {
              const filtered = prev.filter((u) => u.userId !== userData.userId);
              return [...filtered, userData];
            });
          }
        })
      );

      return () => {
        unsubscribes.forEach((unsub) => unsub());
      };
    }
  }, [room, event]);

  useEffect(() => {
    // Calculate countdown to next problem release
    if (!room || !event || !room.kachidokiStatus?.isRunning) {
      setNextReleaseIn(0);
      return;
    }

    const interval = setInterval(async () => {
      if (room.kachidokiStatus?.startTime) {
        const startTime = room.kachidokiStatus.startTime.toMillis();
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        const releaseInterval = event.kachidokiSettings?.releaseInterval || 30;
        const totalProblems = event.problems.length;
        
        // Calculate which problems should be released
        const releasedCount = Math.min(
          Math.floor(elapsedSeconds / releaseInterval) + 1,
          totalProblems
        );
        
        // Check if we need to update released problems
        const currentReleasedProblems = room.kachidokiStatus.releasedProblems || [];
        if (releasedCount > currentReleasedProblems.length && roomId) {
          // Build the releasedProblems array
          const newReleasedProblems: number[] = [];
          for (let i = 0; i < releasedCount; i++) {
            newReleasedProblems.push(i);
          }
          
          // Update room with correct released problems and current problem index
          const updates: any = {
            currentProblemIndex: releasedCount - 1,
            'kachidokiStatus.releasedProblems': newReleasedProblems,
          };
          
          // Calculate next release time if not all problems are released
          if (releasedCount < totalProblems) {
            updates['kachidokiStatus.nextReleaseTime'] = Timestamp.fromMillis(
              startTime + (releasedCount * releaseInterval * 1000)
            );
          }
          
          try {
            await updateDoc(doc(db, 'v3rooms', roomId), updates);
          } catch (error) {
            console.error('Error updating released problems:', error);
          }
        }
        
        // Calculate countdown to next release
        if (releasedCount < totalProblems) {
          const nextReleaseTime = startTime + (releasedCount * releaseInterval * 1000);
          const remaining = Math.max(0, Math.ceil((nextReleaseTime - now) / 1000));
          setNextReleaseIn(remaining);
        } else {
          // All problems released
          setNextReleaseIn(0);
          if (roomId && room.status === 'running') {
            await endKachidoki();
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [room, event, roomId]);

  const startKachidoki = async () => {
    if (!roomId || !room || !event) return;

    const now = serverTimestamp();
    const updates: any = {
      status: 'running',
      startTime: now,
      'kachidokiStatus.isRunning': true,
      'kachidokiStatus.startTime': now,
      'kachidokiStatus.releasedProblems': [0], // Release first problem
      'kachidokiStatus.nextReleaseTime': Timestamp.fromMillis(
        Date.now() + (event.kachidokiSettings?.releaseInterval || 30) * 1000
      ),
      currentProblemIndex: 0,
      displayMode: 'problem',
    };

    await updateDoc(doc(db, 'v3rooms', roomId), updates);
  };

  const pauseKachidoki = async () => {
    if (!roomId || !room) return;

    await updateDoc(doc(db, 'v3rooms', roomId), {
      status: 'paused',
      'kachidokiStatus.isRunning': false,
    });
  };

  const resumeKachidoki = async () => {
    if (!roomId || !room || !event) return;

    // Calculate next release time based on pause duration
    const updates: any = {
      status: 'running',
      'kachidokiStatus.isRunning': true,
    };

    await updateDoc(doc(db, 'v3rooms', roomId), updates);
  };

  const endKachidoki = async () => {
    if (!roomId || !room) return;

    await updateDoc(doc(db, 'v3rooms', roomId), {
      status: 'finished',
      'kachidokiStatus.isRunning': false,
      displayMode: 'finalRanking',
      endTime: serverTimestamp(),
    });
  };

  const resetKachidoki = async () => {
    if (!roomId || !room) return;

    const confirmReset = window.confirm(
      '問題公開状況をリセットしますか？\n' +
      '・すべての問題が未公開状態に戻ります\n' +
      '・参加者の回答データは削除されません\n' +
      '・スコアはそのまま保持されます'
    );

    if (!confirmReset) return;

    await updateDoc(doc(db, 'v3rooms', roomId), {
      status: 'waiting',
      startTime: null,
      currentProblemIndex: -1,
      displayMode: 'waiting',
      'kachidokiStatus.isRunning': false,
      'kachidokiStatus.startTime': null,
      'kachidokiStatus.releasedProblems': [],
      'kachidokiStatus.nextReleaseTime': null,
    });
  };

  const clearAllSubmissions = async () => {
    if (!roomId || !room) return;

    const confirmClear = window.confirm(
      '⚠️ 警告: すべての回答データを削除しますか？\n\n' +
      'この操作により:\n' +
      '・すべての参加者の回答データが削除されます\n' +
      '・スコアが0にリセットされます\n' +
      '・この操作は取り消せません\n\n' +
      '本当に削除しますか？'
    );

    if (!confirmClear) return;

    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      '最終確認: 本当にすべての回答データを削除してよろしいですか？'
    );

    if (!doubleConfirm) return;

    try {
      // Get all submissions for this room
      const submissionsQuery = query(
        collection(db, 'v3submissions'),
        where('roomId', '==', roomId)
      );
      const submissionsSnapshot = await getDocs(submissionsQuery);

      // Delete each submission
      const deletePromises = submissionsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      alert(`${submissionsSnapshot.size}件の回答データを削除しました。`);
    } catch (error) {
      console.error('Error clearing submissions:', error);
      alert('回答データの削除中にエラーが発生しました。');
    }
  };

  const getReleasedCount = () => {
    return room?.kachidokiStatus?.releasedProblems?.length || 0;
  };

  const getTotalProblems = () => {
    return event?.problems?.length || 0;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!room || !event) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>KACHIDOKI 管理画面</Title>
        <StatusBadge status={room.status}>{room.status.toUpperCase()}</StatusBadge>
      </Header>

      <ControlPanel>
        <CardTitle>競技コントロール</CardTitle>
        
        {room.kachidokiStatus?.isRunning && nextReleaseIn > 0 && (
          <CountdownDisplay>
            次の問題公開まで: {formatTime(nextReleaseIn)}
          </CountdownDisplay>
        )}

        <ButtonGroup>
          {room.status === 'waiting' && (
            <Button variant="success" onClick={startKachidoki}>
              競技開始
            </Button>
          )}
          
          {room.status === 'running' && (
            <Button variant="warning" onClick={pauseKachidoki}>
              一時停止
            </Button>
          )}
          
          {room.status === 'paused' && (
            <Button variant="success" onClick={resumeKachidoki}>
              再開
            </Button>
          )}
          
          {(room.status === 'running' || room.status === 'paused') && (
            <Button variant="danger" onClick={endKachidoki}>
              競技終了
            </Button>
          )}
          
          {(room.status === 'finished' || room.status === 'paused') && (
            <Button onClick={resetKachidoki}>
              リセット
            </Button>
          )}
        </ButtonGroup>
      </ControlPanel>

      <MainContent>
        <Card>
          <CardTitle>イベント情報</CardTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>イベント名</InfoLabel>
              <InfoValue style={{ fontSize: '16px' }}>{event.eventName}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>参加者数</InfoLabel>
              <InfoValue>{participants.length}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>公開済み問題</InfoLabel>
              <InfoValue>{getReleasedCount()} / {getTotalProblems()}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>公開間隔</InfoLabel>
              <InfoValue>{event.kachidokiSettings?.releaseInterval}秒</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Card>

        <Card>
          <CardTitle>問題公開状況</CardTitle>
          <ProgressBar>
            <ProgressFill percent={(getReleasedCount() / getTotalProblems()) * 100}>
              {Math.round((getReleasedCount() / getTotalProblems()) * 100)}%
            </ProgressFill>
          </ProgressBar>
          <ProblemStatus>
            {event.problems.map((problem, index) => {
              const isReleased = room.kachidokiStatus?.releasedProblems?.includes(index);
              const isCurrent = index === room.currentProblemIndex;
              return (
                <ProblemIndicator
                  key={problem.problemId}
                  status={isCurrent ? 'current' : isReleased ? 'released' : 'locked'}
                >
                  問題 {index + 1}
                </ProblemIndicator>
              );
            })}
          </ProblemStatus>
        </Card>
      </MainContent>

      <Card>
        <CardTitle>スクリーン表示</CardTitle>
        <ButtonGroup>
          <Button onClick={() => window.open(`/v3/${room.projectId}/room/${roomId}/screen`, '_blank')}>
            スクリーンを開く
          </Button>
          <Button onClick={() => window.open(`/v3/${room.projectId}/room/${roomId}/ranking`, '_blank')}>
            ランキング画面を開く
          </Button>
        </ButtonGroup>
      </Card>

      <Card>
        <CardTitle>管理操作</CardTitle>
        <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
          ※これらの操作は慎重に行ってください
        </div>
        <ButtonGroup>
          <Button 
            variant="danger" 
            onClick={clearAllSubmissions}
            disabled={room.status === 'running'}
          >
            回答データをすべて削除
          </Button>
        </ButtonGroup>
        {room.status === 'running' && (
          <div style={{ marginTop: '10px', color: '#dc3545', fontSize: '14px' }}>
            ※競技中は回答データを削除できません
          </div>
        )}
      </Card>
    </Container>
  );
};

export default AdminKachidokiManager;